import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Clock, AlertTriangle, CalendarClock, ListTodo, User } from 'lucide-react';

const PRIORITY_COLORS = {
  High: 'bg-red-100 text-red-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-slate-100 text-slate-600',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function bucketOf(t) {
  if (t.status === 'Completed') return 'completed';
  if (!t.deadline) return 'upcoming';
  const diff = Math.ceil((new Date(t.deadline) - new Date(todayStr())) / 86400000);
  if (diff < 0) return 'overdue';
  if (diff <= 3) return 'due_soon';
  return 'upcoming';
}

const TaskCard = ({ t, onComplete, completing }) => {
  const bucket = bucketOf(t);
  return (
    <div data-testid={`my-task-${t._id}`} className={`rounded-lg border p-4 bg-white ${bucket === 'overdue' ? 'border-red-200' : bucket === 'due_soon' ? 'border-amber-200' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-slate-800 truncate">{t.title}</h4>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.Low}`}>{t.priority}</span>
            {t.status === 'Completed' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">Completed</span>}
          </div>
          {t.description && <p className="text-xs text-slate-500 mt-1">{t.description}</p>}
          <div className="flex items-center gap-3 flex-wrap mt-2 text-[11px] text-slate-400">
            <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {t.assigned_by_name || 'Unknown'}</span>
            <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Due {t.deadline || '—'}</span>
            {t.duration ? <span>Est. {t.duration} {t.duration_unit || 'hours'}</span> : null}
          </div>
        </div>
        {t.status !== 'Completed' && (
          <Button data-testid={`complete-task-${t._id}`} onClick={() => onComplete(t)} disabled={completing} className="h-8 shrink-0 bg-green-600 hover:bg-green-700 text-white text-xs">
            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> {completing ? '...' : 'Mark Done'}
          </Button>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, icon: Icon, color, tasks, onComplete, completingId }) => {
  if (tasks.length === 0) return null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <h3 className={`text-sm font-bold uppercase tracking-wider ${color}`}>{title}</h3>
        <span className="text-xs text-slate-400">({tasks.length})</span>
      </div>
      <div className="space-y-2">
        {tasks.map(t => <TaskCard key={t._id} t={t} onComplete={onComplete} completing={completingId === t._id} />)}
      </div>
    </div>
  );
};

export default function MyTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.getMyTasks();
      setTasks(data || []);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleComplete = async (t) => {
    setCompletingId(t._id);
    try {
      await api.completeTask(t._id, todayStr());
      toast.success('Task marked as completed');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to complete task');
    } finally {
      setCompletingId(null);
    }
  };

  const grouped = { overdue: [], due_soon: [], upcoming: [], completed: [] };
  tasks.forEach(t => grouped[bucketOf(t)].push(t));

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6" data-testid="my-tasks-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-blue-600">My Work</p>
        <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>My Tasks</h1>
        <p className="text-sm text-slate-500 mt-1">Tasks assigned to you. Mark them done as you complete them.</p>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="my-tasks-summary">
        <Card className="border-red-200"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-red-500">Overdue</p><p className="text-2xl font-black text-red-600" data-testid="count-overdue">{grouped.overdue.length}</p></CardContent></Card>
        <Card className="border-amber-200"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-amber-500">Due Soon</p><p className="text-2xl font-black text-amber-600" data-testid="count-due-soon">{grouped.due_soon.length}</p></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Upcoming</p><p className="text-2xl font-black text-slate-700" data-testid="count-upcoming">{grouped.upcoming.length}</p></CardContent></Card>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 text-center py-12">Loading your tasks...</p>
      ) : tasks.length === 0 ? (
        <div className="text-center py-16" data-testid="my-tasks-empty">
          <ListTodo className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No tasks assigned to you yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <Section title="Overdue" icon={AlertTriangle} color="text-red-600" tasks={grouped.overdue} onComplete={handleComplete} completingId={completingId} />
          <Section title="Due Soon" icon={Clock} color="text-amber-600" tasks={grouped.due_soon} onComplete={handleComplete} completingId={completingId} />
          <Section title="Upcoming" icon={CalendarClock} color="text-slate-600" tasks={grouped.upcoming} onComplete={handleComplete} completingId={completingId} />
          <Section title="Completed" icon={CheckCircle2} color="text-green-600" tasks={grouped.completed} onComplete={handleComplete} completingId={completingId} />
        </div>
      )}
    </div>
  );
}
